/**
 * Sezioni "Il Mio Conto" per la striscia in alto (vociBarra.js), una voce di
 * menu per ogni voce del catalogo che sta nel gettone (utils/permessi.js).
 */
import { haLaVoce, haUnaVoce } from './permessi';
import { skinConCasino, skinConScommesse } from '../moduli/leggi';
import i18next from 'i18next';

/** Icone FontAwesome per le card (nome icona) */
const ICONS = {
  list: 'list',
  listOl: 'list-ol',
  wallet: 'wallet',
  arrowDown: 'arrow-down',
  arrowUp: 'arrow-up',
  exchangeAlt: 'exchange-alt',
  user: 'user',
  userPlus: 'user-plus',
  key: 'key',
  envelope: 'envelope',
  slidersH: 'sliders-h',
  users: 'users',
  chartPie: 'chart-pie',
  ticketAlt: 'ticket-alt',
  balanceScale: 'balance-scale',
  percent: 'percent',
  shieldAlt: 'shield-alt',
  asterisk: 'asterisk',
  coins: 'coins',
  handHoldingUsd: 'hand-holding-usd',
  gift: 'gift',
  fileInvoiceDollar: 'file-invoice-dollar',
};

/**
 * OGNI VOCE DEL MENU CHIEDE LA SUA VOCE DEL CATALOGO, lato FRONTEND, letta
 * dal gettone con haLaVoce(): sono quelle di utils/permessi.js, e la regola
 * e' una sola, chi ha la voce vede la cosa.
 *
 * Questo menu era stato scritto con nomi propri — movements.read,
 * users.create e simili — tradotti in voci del backoffice, e una voce sola
 * copriva piu' cose: movimenti, giocate e report sotto la stessa; la lista
 * della rete e la propria scheda sotto la stessa. Negarne una a un livello
 * gli toglieva anche le altre. E profilo, password, cassa e messaggi non
 * chiedevano niente: si vedevano e basta, senza modo di toglierle.
 *
 * Adesso ogni cosa ha la sua voce, anche quelle che prima erano libere (la
 * migrazione le ha dette «si'» a tutta la rete, quindi chi le vedeva le vede
 * ancora): si tolgono a un livello come tutte le altre.
 *
 * @returns {Array<{ sectionTitle: string, items: Array<{ label: string, path: string, icon: string, openCreateModal?: boolean }> }>}
 */
export function getMyAccountSections() {
  const sections = [];

  // ---------- PANNELLO ADMIN ----------
  const panelAdminItems = [];

  if (haLaVoce('SITE_NETWORK_VIEW')) {
    panelAdminItems.push({ get label() { return i18next.t('Lista utenti'); }, path: '/my-account/lista-utenti', icon: ICONS.users });
  }

  // Nuovo Utente e' un bottone dentro Lista utenti, non una pagina sua.
  if (haLaVoce('SITE_USER_CREATE')) {
    panelAdminItems.push({ get label() { return i18next.t('Nuovo utente'); }, path: '/my-account/lista-utenti', icon: ICONS.userPlus, openCreateModal: true });
  }

  if (haLaVoce('SITE_TRANSFER')) {
    panelAdminItems.push({ get label() { return i18next.t('Giroconto'); }, path: '/my-account/giroconto', icon: ICONS.exchangeAlt });
  }

  // Il report sta nel pannello admin e guarda la rete, non la persona:
  // ha la sua voce, separata da movimenti e giocate.
  // E' il report del casino': su una skin senza casino' non c'e' niente da guardare.
  if (haLaVoce('SITE_REPORTS_VIEW') && skinConCasino()) {
    panelAdminItems.push({ get label() { return i18next.t('Report'); }, path: '/my-account/report', icon: ICONS.chartPie });
  }

  if (haUnaVoce(['SITE_COMMISSIONS_VIEW', 'SITE_COMMISSIONS_MANAGE'])) {
    panelAdminItems.push({ get label() { return i18next.t('Commissioni'); }, path: '/my-account/commissioni', icon: ICONS.fileInvoiceDollar });
  }

  if (panelAdminItems.length > 0) {
    sections.push({ get sectionTitle() { return i18next.t('PANNELLO ADMIN'); }, items: panelAdminItems });
  }

  // ---------- DETTAGLIO GIOCATE ----------
  const dettaglioItems = [];

  if (haLaVoce('SITE_BETS_VIEW') && skinConScommesse()) {
    dettaglioItems.push({ get label() { return i18next.t('Lista scommesse'); }, path: '/my-account/lista-scommesse', icon: ICONS.list });
  }

  if (haLaVoce('SITE_MOVEMENTS_VIEW')) {
    dettaglioItems.push({ get label() { return i18next.t('Lista movimenti'); }, path: '/my-account/lista-movimenti', icon: ICONS.listOl });
  }

  if (dettaglioItems.length > 0) {
    sections.push({ get sectionTitle() { return i18next.t('DETTAGLIO GIOCATE'); }, items: dettaglioItems });
  }

  // ---------- QUOTE ----------
  // Le quote sono quelle dello sport: servono solo dove la skin ha lo sport.
  if (haLaVoce('SITE_ODDS_VIEW') && skinConScommesse()) {
    sections.push({
      get sectionTitle() { return i18next.t('QUOTE'); },
      items: [{ get label() { return i18next.t('Quote'); }, path: '/odds', icon: ICONS.percent }],
    });
  }

  // ---------- PAGAMENTI e TRASFERIMENTI ----------
  // Versare e prelevare sono due voci: chi ne ha una sola vede solo quella.
  // La Cassa e' la pagina dietro a tutte e due, e la vede chi ne ha almeno
  // una: dentro, mostra solo la linguetta di cui si ha la voce.
  const pagamentiItems = [];

  if (haLaVoce('SITE_DEPOSIT')) {
    pagamentiItems.push({ get label() { return i18next.t('Versamento'); }, path: '/my-account/versamento', icon: ICONS.arrowDown });
  }

  if (haLaVoce('SITE_WITHDRAW')) {
    pagamentiItems.push({ get label() { return i18next.t('Prelievo'); }, path: '/my-account/prelievo', icon: ICONS.arrowUp });
  }

  if (pagamentiItems.length > 0) {
    sections.push({ get sectionTitle() { return i18next.t('PAGAMENTI'); }, items: pagamentiItems });
    sections.push({
      get sectionTitle() { return i18next.t('TRASFERIMENTI'); },
      items: [{ get label() { return i18next.t('Cassa'); }, path: '/my-account/cassa', icon: ICONS.wallet }],
    });
  }

  // ---------- DATI DEL CONTO ----------
  const datiItems = [];

  if (haLaVoce('SITE_PROFILE_VIEW')) {
    datiItems.push({ get label() { return i18next.t('Il mio profilo'); }, path: '/my-account/profilo', icon: ICONS.user });
  }

  /* Le tre voci della sicurezza - password, PIN, antieffrazione - erano tre
     pagine con un campo ciascuna, e due su tre erano segnaposto: del PIN e
     dell'antieffrazione non esiste una riga di codice in nessun servizio.
     Resta la password, che e' vera, nella pagina Sicurezza; i tre indirizzi
     vecchi portano li' (Main.js). Una voce sola, con la sua voce del catalogo. */
  if (haLaVoce('SITE_PASSWORD_MANAGE')) {
    datiItems.push({ get label() { return i18next.t('Sicurezza'); }, path: '/my-account/sicurezza', icon: ICONS.key });
  }

  if (datiItems.length > 0) {
    sections.push({ get sectionTitle() { return i18next.t('DATI DEL CONTO'); }, items: datiItems });
  }

  // ---------- MESSAGGISTICA ----------
  /* Gli avvisi e le promozioni che la piattaforma manda al cliente
     (notification-service, /api/messaggi), e le preferenze su cosa
     ricevere fuori dal sito. Una voce del catalogo per tutte e due: chi
     legge i messaggi sceglie anche se riceverli per email o su Telegram. */
  if (haLaVoce('SITE_MESSAGES_VIEW')) {
    sections.push({
      get sectionTitle() { return i18next.t('MESSAGGISTICA'); },
      items: [
        { get label() { return i18next.t('Messaggi'); }, path: '/my-account/messaggi', icon: ICONS.envelope },
        { get label() { return i18next.t('Preferenze'); }, path: '/my-account/messaggi/preferenze', icon: ICONS.slidersH },
      ],
    });
  }

  return sections;
}
